import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
  ExpressionSpecification,
  DataDrivenPropertyValueSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  CircleLayerSpecification,
} from "mapbox-gl";

export type ThemeWithOptionalProperties = Partial<
  (
    | FillLayerSpecification
    | LineLayerSpecification
    | CircleLayerSpecification
  ) & {
    // use own types until the types library gets updated
    id: MapboxDraw.ThemeLayerId | "gl-draw-polygon-fill" | "gl-draw-lines";
  }
>;

const checkValidityExpression = [
  "boolean",
  ["get", "user_valid"],
  true,
] as ExpressionSpecification;
const errorColor = "red";
const getValidityExpression = (
  color: string,
): DataDrivenPropertyValueSpecification<string> => {
  return ["case", checkValidityExpression, color, errorColor];
};

const themeOverrides: Array<ThemeWithOptionalProperties> = [
  {
    id: "gl-draw-polygon-fill",
    type: "fill",
    filter: ["all", ["==", "$type", "Polygon"]],
    paint: {
      "fill-color": getValidityExpression("#AED7BE"),
      "fill-outline-color": "transparent",
      "fill-opacity": 0.3,
    },
  },
  {
    // fix for https://github.com/mapbox/mapbox-gl-draw/issues/1357
    id: "gl-draw-lines",
    paint: {
      "line-dasharray": ["literal", [1, 2]],
    },
  },
  {
    id: "gl-draw-polygon-stroke-inactive",
    type: "line",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Polygon"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "line-color": getValidityExpression("#50BE64"),
      "line-dasharray": ["literal", [2, 0]],
    },
  },
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: [
      "all",
      ["==", "active", "true"],
      ["==", "$type", "Polygon"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "line-color": getValidityExpression("#fff"),
      "line-dasharray": ["literal", [1, 2]],
      "line-width": 2,
    },
  },
  {
    id: "gl-draw-line-active",
    type: "line",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "LineString"]],
    paint: {
      "line-color": "#fff",
    },
  },
  {
    id: "gl-draw-line-inactive",
    type: "line",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "LineString"]],
    paint: {
      "line-color": "transparent",
    },
  },
  {
    id: "gl-draw-polygon-midpoint",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
    paint: {
      "circle-radius": 5,
      "circle-color": "#FFFFFF",
    },
  },
  {
    id: "gl-draw-point-active",
    type: "circle",
    filter: [
      "all",
      ["==", "$type", "Point"],
      ["!=", "meta", "midpoint"],
      ["==", "active", "true"],
    ],
    paint: {
      "circle-radius": 10,
      "circle-color": "#618C85",
      "circle-stroke-color": "#fff",
      "circle-stroke-width": 2,
    },
  },
  {
    id: "gl-draw-polygon-and-line-vertex-stroke-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "meta", "vertex"],
      ["==", "$type", "Point"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 8,
      "circle-color": "#fff",
    },
  },
  {
    id: "gl-draw-polygon-fill-static",
    type: "fill",
    filter: ["all", ["==", "mode", "static"], ["==", "$type", "Polygon"]],
    paint: {
      "fill-color": "#AED7BE",
      "fill-opacity": 0.33,
    },
  },
];

export const getThemeWithOverrides = (
  overrides = themeOverrides,
): Array<ThemeWithOptionalProperties> => {
  const themeMap = new Map(
    MapboxDraw.lib.theme.map((style) => [style.id, style]),
  );

  for (const style of overrides) {
    if (!style?.id) {
      continue;
    }

    // mapbox-gl-draw types are outdated
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const existing = themeMap.get(style.id);

    if (existing) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      themeMap.set(style.id, {
        ...existing,
        ...style,

        paint: {
          ...(existing.paint || {}),
          ...(style.paint || {}),
        },
        layout: {
          ...(existing.layout || {}),
          ...(style.layout || {}),
        },
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      themeMap.set(style.id, style);
    }
  }

  return Array.from(themeMap.values());
};
